import React, { useEffect, useState } from "react";
import { Toaster, toast } from "sonner";
import DashboardShell from "../../components/layout/DashboardShell";
import api, { formatApiError } from "../../lib/api";
import { Bell, RefreshCw, Play, Search } from "lucide-react";

const KIND_LABEL = {
  hot_lead_inactive_24h: "Hot lead · 24h inactive",
  followup_overdue: "Follow-up overdue",
  sanction_received: "Sanction received",
  case_disbursed: "Case disbursed",
  mandate_pending_signature: "Mandate pending signature",
  target_achievement_50: "Target 50% hit",
  target_achievement_100: "Target 100% hit",
};

/**
 * Admin console for automated Debt CRM notifications.
 *
 * - Left: recent notification events (kind, recipient, status, dedupe key)
 * - Right: run-rules trigger (dry-run + real)
 * Rules currently supported: hot_lead_inactive_24h, followup_overdue,
 * sanction_received (see backend/routes/debt_phase4.py).
 */
export default function DebtNotifications() {
  const [rows, setRows] = useState([]);
  const [kinds, setKinds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [filter, setFilter] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/notifications");
      setRows(data.items || []);
      setKinds(data.kinds || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const runRules = async (dry) => {
    setRunning(true);
    try {
      const { data } = await api.post("/admin/notifications/run-rules", null, { params: { dry_run: dry } });
      const wf = data.would_fire ?? (data.fired + data.deduped);
      toast.success(
        dry
          ? `Dry-run: ${wf} would trigger · ${data.fired} new · ${data.deduped} already sent`
          : `Fired ${data.fired} · deduped ${data.deduped}`
      );
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setRunning(false); }
  };

  const filtered = rows.filter((r) => {
    if (!filter) return true;
    const q = filter.toLowerCase();
    return (r.to_email || "").toLowerCase().includes(q)
      || (r.subject || "").toLowerCase().includes(q)
      || (r.kind || "").toLowerCase().includes(q);
  });

  return (
    <DashboardShell title="Debt Notifications" subtitle="Admin · Automated triggers · Email + WhatsApp (mocked)">
      <Toaster richColors position="top-right" />
      <div className="space-y-4" data-testid="admin-debt-notifications">
        {/* Toolbar */}
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2 text-xs text-ink-muted">
            <Bell size={13} className="text-navy" /> {rows.length} events · {kinds.length} rule types
          </div>
          <div className="flex gap-2">
            <button onClick={load} className="text-[11px] font-heading text-navy hover:bg-navy/10 rounded px-2 py-1 border border-navy/30 inline-flex items-center gap-1" data-testid="notif-refresh">
              <RefreshCw size={11} /> Refresh
            </button>
            <button onClick={() => runRules(true)} disabled={running} className="text-[11px] font-heading text-navy hover:bg-navy/10 rounded px-2 py-1 border border-navy/30 inline-flex items-center gap-1 disabled:opacity-40" data-testid="notif-dryrun">
              <Play size={11} /> Dry-run
            </button>
            <button onClick={() => runRules(false)} disabled={running} className="text-[12px] font-heading text-navy bg-yellow-400 hover:bg-yellow-500 rounded px-3 py-1.5 inline-flex items-center gap-1 disabled:opacity-40" data-testid="notif-run">
              <Play size={12} /> {running ? "Running…" : "Run rules now"}
            </button>
          </div>
        </div>

        <div className="border border-ink/10 rounded-lg bg-white p-3 text-[11px] text-ink-muted flex items-center gap-2">
          <Search size={11} />
          <input
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Filter by recipient, subject or kind…"
            data-testid="notif-filter"
            className="flex-1 outline-none bg-transparent"
          />
        </div>

        <div className="border border-ink/10 rounded-lg bg-white overflow-auto">
          <table className="w-full text-[12px]">
            <thead className="bg-navy/5 text-navy">
              <tr>
                <th className="text-left px-3 py-2 border-b border-ink/10">When</th>
                <th className="text-left px-3 py-2 border-b border-ink/10">Kind</th>
                <th className="text-left px-3 py-2 border-b border-ink/10">Recipient</th>
                <th className="text-left px-3 py-2 border-b border-ink/10">Subject</th>
                <th className="text-left px-3 py-2 border-b border-ink/10">Email</th>
                <th className="text-left px-3 py-2 border-b border-ink/10">WhatsApp</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={6} className="p-4 text-xs italic text-ink-muted">Loading…</td></tr>}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan={6} className="p-4 text-xs italic text-ink-muted">No notifications yet. Click <b>Run rules now</b> to scan and fire triggers.</td></tr>
              )}
              {filtered.map((r) => (
                <tr key={r.event_id} className="border-b border-ink/5" data-testid={`notif-row-${r.event_id}`}>
                  <td className="px-3 py-2 font-mono text-[10px] text-ink-muted">{r.created_at?.slice(0, 19).replace("T", " ")}</td>
                  <td className="px-3 py-2 text-[11px]">{KIND_LABEL[r.kind] || r.kind}</td>
                  <td className="px-3 py-2 font-mono text-[11px]">{r.to_email}</td>
                  <td className="px-3 py-2 text-[11px] max-w-[380px] truncate" title={r.subject}>{r.subject}</td>
                  <td className="px-3 py-2">
                    <StatusPill status={r.email_status} />
                  </td>
                  <td className="px-3 py-2">
                    <StatusPill status={r.whatsapp_status} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="text-[10px] text-ink-muted italic">
          WhatsApp is stubbed for now — logs preview only; live gateway integration pending user gateway pick.
        </div>
      </div>
    </DashboardShell>
  );
}

function StatusPill({ status }) {
  const s = (status || "").toLowerCase();
  const map = {
    sent: "bg-emerald-100 text-emerald-800",
    queued: "bg-amber-100 text-amber-800",
    failed: "bg-red-100 text-red-800",
    stub: "bg-ink/10 text-ink-muted",
  };
  const cls = map[s] || "bg-ink/5 text-ink-muted";
  return <span className={`text-[10px] font-heading px-1.5 py-0.5 rounded-full uppercase ${cls}`}>{status || "—"}</span>;
}
